
import { defineComponent, onBeforeMount, reactive } from 'vue'

// components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import FailedFetch from '@/common/components/UI/FailedFetch/index.vue'
import TableScrollWrapper from '@/common/components/UI/TableScrollWrapper/index.vue'
import TheTable from '@/common/components/UI/TheTable/index.vue'
import Cheap from '@/common/components/UI/Cheap/index.vue'

// module components
import SlaActionsFtl from '@/modules/SLAModule/components/SlaActionsFtl/index.vue'
import SLAFilters from '@/modules/SLAModule/components/SLAFilters/index.vue'

// realisation
import { SLA } from '@/core/realisations/sla.realisation'

// types
import { SlaCreatePayloadT, SlaGetListParams, SlaT } from '@/core/types/sla.types'

// classes
import { DataState } from '@/core/classes/DataState.class'

// composable
import useActions from '@/common/composable/useActions'
import useNotifications from '@/common/composable/useNotifications'
import useAccount from '@/common/composable/useAccount'

// constants
import { SLAColumns } from '@/core/constants/columns/SLA.columns'
import { useFile } from '@/common/composable/useFile'

export default defineComponent({
    components: {
        PageTitle,
        TheButton,
        FailedFetch,
        SlaActionsFtl,
        TheTable,
        TableScrollWrapper,
        Cheap,
        'sla-filters': SLAFilters,
    },
    setup() {
        const { havePermissions } = useAccount()
        const { saveFile } = useFile()

        const state = reactive(new DataState<SlaT>({}))
        const notifications = useNotifications()

        const filters_state = reactive({
            fromCityId: null as number | null,
            toCityId: null as number | null,
            shipmentTypeId: null as number | null,
            periodId: null as number | null,
            transitCities: [] as number[],
            //
            limit: 50,
        })

        const loadSLA = async (page: number) => {
            state.pagination.current_page = page

            const params = {
                page,
                fromCityId: filters_state.fromCityId,
                toCityId: filters_state.toCityId,
                shipmentTypeId: filters_state.shipmentTypeId,
                periodId: filters_state.periodId,
                transitCities: filters_state.transitCities,
                limit: filters_state.limit,
            }

            try {
                state.is_loading = true
                const data = await SLA.getListFtl(params)

                state.list = data.data

                state.pagination.total_pages = data.meta.last_page
                state.pagination.total_items = data.meta.total

                state.is_failed = false
                state.failure.error_state.is_cancel = false
            } catch (error) {
                state.failure.catchError(error)

                if (state.failure.error_state.is_cancel) {
                    state.is_failed = true
                }
            } finally {
                if (!state.failure.error_state.is_cancel) {
                    state.is_loading = false
                }
            }
        }

        const onLimitChange = (limit: number) => {
            filters_state.limit = limit
            loadSLA(state.pagination.current_page)
        }

        onBeforeMount(() => loadSLA(1))

        const { actions_state, startCreate, startUpdate, startDelete } = useActions<SlaT>()

        const createSLA = async ({
            city_from,
            city_to,
            pickup,
            processing,
            transit,
            delivery,
            tariff,
            supplier_id,
            is_active,
            periodId,
            transit_cities,
        }: {
            city_from: number
            city_to: number
            pickup: number
            processing: number
            transit: number
            delivery: number
            tariff: string
            supplier_id: number
            is_active: boolean
            periodId: number
            transit_cities: number[]
        }) => {
            try {
                actions_state.is_actions_loading = true

                const params: SlaCreatePayloadT = {
                    fromCityId: city_from,
                    toCityId: city_to,
                    pickup,
                    processing,
                    transit,
                    delivery,
                    tariff,
                    isActive: is_active,
                    transitCities: transit_cities,
                }

                if (supplier_id) {
                    params.supplierId = supplier_id
                }
                if (periodId) {
                    params.periodId = periodId
                }

                await SLA.createSlaFtl(params)

                actions_state.is_actions_opened = false
                notifications.notify({ title: 'SLA успешно создан!', type: 'success' })

                loadSLA(1)
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: `Ошибка создания SLA`,
                })
            } finally {
                actions_state.is_actions_loading = false
            }
        }

        const updateSLA = async ({
            id,
            city_from,
            city_to,
            pickup,
            processing,
            transit,
            delivery,
            tariff,
            supplier_id,
            is_active,
            periodId,
            transit_cities,
        }: {
            id: number
            city_from: number
            city_to: number
            pickup: number
            processing: number
            transit: number
            delivery: number
            tariff: string
            supplier_id: number
            is_active: boolean
            periodId: number
            transit_cities: number[]
        }) => {
            try {
                actions_state.is_actions_loading = true

                const params: SlaCreatePayloadT = {
                    fromCityId: city_from,
                    toCityId: city_to,
                    pickup,
                    processing,
                    transit,
                    delivery,
                    tariff,
                    isActive: is_active,
                    transitCities: transit_cities,
                }

                if (supplier_id) {
                    params.supplierId = supplier_id
                }
                if (periodId) {
                    params.periodId = periodId
                }

                await SLA.updateSlaFtl(id, params)

                actions_state.is_actions_opened = false
                notifications.notify({ title: 'SLA успешно отредактирован!', type: 'success' })

                loadSLA(1)
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: `Ошибка редактирования SLA`,
                })
            } finally {
                actions_state.is_actions_loading = false
            }
        }

        const deleteSLA = async (sla_id: number) => {
            try {
                actions_state.is_actions_loading = true

                await SLA.deleteSlaFtl(sla_id)
                notifications.notify({ title: 'SLA успешно удален!', type: 'success' })

                actions_state.is_actions_opened = false

                loadSLA(1)
            } catch (error) {
                console.error(error)
            } finally {
                actions_state.is_actions_loading = false
            }
        }

        //

        const applyFilters = (params: SlaGetListParams) => {
            filters_state.fromCityId = params.fromCityId || null
            filters_state.toCityId = params.toCityId || null
            filters_state.shipmentTypeId = params.shipmentTypeId || null
            filters_state.periodId = params.periodId || null
            filters_state.transitCities = params.transitCities || []

            loadSLA(1)
        }

        const resetFilters = () => {
            filters_state.fromCityId = null
            filters_state.toCityId = null
            filters_state.shipmentTypeId = null
            filters_state.periodId = null
            filters_state.transitCities = []

            loadSLA(1)
        }

        //

        return {
            havePermissions,
            state,
            filters_state,
            actions_state,
            startCreate,
            startUpdate,
            startDelete,
            createSLA,
            updateSLA,
            deleteSLA,
            onLimitChange,
            loadSLA,
            //
            applyFilters,
            resetFilters,
            //
            SLAColumns,
        }
    },
})
